import React, { useEffect, useState } from 'react'
import { getTouchpoints, patchTouchpoints, type TouchSummary, type Touchpoints } from './crmApi'
import { IconChatUi, IconLink, IconMailIn, IconPhone, crmToast } from './crmUi'

// Row-sized mono icons for the two touchpoints without one in the house kit.
const IconSmartphone: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="7" y="2" width="10" height="20" rx="2" /><path d="M11 18h2" /></svg>
)
const IconMailList: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M3 7h18M3 12h18M3 17h12" /></svg>
)

/**
 * TOUCH ICONS (Lee, 17 Sep 2026 v2 — icons instead of dots) — per-row
 * touchpoint health in RANK order: LinkedIn › Mobile › Email › Mailing list ›
 * Telephone. GREEN = on record · GREY = unavailable · faded grey = mailing
 * list not checked yet. Hover for the breakdown + weighted score
 * (Li5 Mo4 Em3 ML2 Te1, /15).
 */
export const TouchDots: React.FC<{ t?: TouchSummary }> = ({ t }) => {
  if (!t) return null
  const items: { on: boolean | null; label: string; Icon: React.FC<{ className?: string }> }[] = [
    { on: t.li, label: 'LinkedIn', Icon: IconLink },
    { on: t.mo, label: 'Mobile', Icon: IconSmartphone },
    { on: t.em, label: 'Email', Icon: IconMailIn },
    { on: t.ml, label: 'Mailing list', Icon: IconMailList },
    { on: t.te, label: 'Telephone', Icon: IconPhone },
  ]
  const title = items.map((d) => `${d.label}: ${d.on === null ? 'not checked yet' : d.on ? '✓' : 'unavailable'}`).join(' · ') + ` · score ${t.score}/15`
  return (
    <span className="inline-flex items-center gap-1.5" title={title}>
      {items.map(({ on, label, Icon }) => (
        <Icon
          key={label}
          className={`w-3.5 h-3.5 shrink-0 ${on === true ? 'text-emerald-600' : on === null ? 'text-txt-tertiary/25' : 'text-txt-tertiary/45'}`}
        />
      ))}
      <span className="text-[10px] font-semibold text-txt-tertiary tabular-nums ml-0.5">{t.count}/5</span>
    </span>
  )
}

/**
 * TOUCHPOINTS card (Lee, 17 Sep 2026) — the five ways to reach a lead:
 * Email · Telephone · Mobile · LinkedIn · WhatsApp. Shows what's on record
 * (n/5), inline-edits each, and for Email asks BREVO whether the address is
 * actually on the mailing list and receiving sends.
 */

const fmtAgo = (iso: string | null | undefined) => {
  if (!iso) return null
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)
  return days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days}d ago`
}

const Row: React.FC<{
  icon: React.ReactNode; label: string; value: string | null; hint?: string
  href?: string | null; onSave: (v: string | null) => void
}> = ({ icon, label, value, hint, href, onSave }) => {
  const [editing, setEditing] = useState(false)
  const [val, setVal] = useState(value || '')
  useEffect(() => { setVal(value || '') }, [value])
  const save = () => { setEditing(false); if ((val.trim() || null) !== (value || null)) onSave(val.trim() || null) }
  return (
    <div className="flex items-center gap-2.5 py-1.5">
      <span className={`inline-flex w-6 h-6 rounded-md items-center justify-center border shrink-0 ${value ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-border text-txt-tertiary'}`}>{icon}</span>
      <span className="text-[11px] font-semibold text-txt-secondary w-20 shrink-0">{label}</span>
      {editing ? (
        <input
          autoFocus value={val} onChange={(e) => setVal(e.target.value)} onBlur={save}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false) }}
          className="flex-1 min-w-0 px-2 py-1 rounded border border-border bg-surface text-[12px] outline-none focus:border-brand-500"
        />
      ) : value ? (
        <span className="flex-1 min-w-0 flex items-center gap-2">
          {href ? (
            <a href={href} target="_blank" rel="noreferrer" className="text-[12px] text-brand-600 hover:underline truncate">{value}</a>
          ) : (
            <span className="text-[12px] text-txt-primary truncate">{value}</span>
          )}
          {hint && <span className="text-[10px] text-txt-tertiary shrink-0">{hint}</span>}
          <button onClick={() => setEditing(true)} className="text-[10px] text-txt-tertiary hover:text-brand-600 hover:underline shrink-0">edit</button>
        </span>
      ) : (
        <button onClick={() => setEditing(true)} className="text-[11px] text-txt-tertiary hover:text-brand-600 hover:underline">+ add</button>
      )}
    </div>
  )
}

export const DealTouchpoints: React.FC<{ dealId: string }> = ({ dealId }) => {
  const [tp, setTp] = useState<Touchpoints | null>(null)
  const load = () => getTouchpoints(dealId).then(setTp).catch(() => {})
  useEffect(() => { setTp(null); load() }, [dealId]) // eslint-disable-line react-hooks/exhaustive-deps

  const save = (field: 'email' | 'telephone' | 'mobile' | 'linkedin' | 'whatsapp') => (v: string | null) =>
    patchTouchpoints(dealId, { [field]: v })
      .then((t) => { setTp(t); crmToast('Touchpoint saved', 'success') })
      .catch((e: any) => crmToast(e?.message || 'Could not save (add a contact to the lead first)', 'error'))

  if (!tp) return null
  const b = tp.email.brevo
  const brevoLine = !tp.email.available ? null
    : !b.configured ? { text: 'Brevo not connected (set BREVO_API_KEY)', tone: 'text-txt-tertiary' }
    : b.error ? { text: `Brevo check failed: ${b.error}`, tone: 'text-amber-600' }
    : b.found === false ? { text: 'NOT on the Brevo mailing list', tone: 'text-amber-600' }
    : b.blacklisted ? { text: 'On Brevo but BLACKLISTED (unsubscribed/bounced) — emails are not being sent', tone: 'text-red-600' }
    : { text: `On the mailing list${b.lists?.length ? ` (${b.lists.join(', ')})` : ''} · ${b.sends90d || 0} email${(b.sends90d || 0) === 1 ? '' : 's'} in 90d${b.lastEmailAt ? ` · last ${fmtAgo(b.lastEmailAt)}` : ' · none sent yet'}`, tone: 'text-emerald-700' }

  return (
    <div className="rounded-xl border border-border p-4">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[11px] font-bold uppercase tracking-wider text-txt-tertiary">Touchpoints</p>
        <span className={`text-[11px] font-bold tabular-nums px-2 py-0.5 rounded-full border ${tp.availableCount >= 4 ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : tp.availableCount >= 2 ? 'border-border text-txt-secondary' : 'border-amber-300 bg-amber-50 text-amber-700'}`}
          title="How many of the five contact routes are on record">
          {tp.availableCount}/5
        </span>
      </div>
      <Row icon={<IconMailIn className="w-3.5 h-3.5" />} label="Email" value={tp.email.value} onSave={save('email')} href={tp.email.value ? `mailto:${tp.email.value}` : null} />
      {brevoLine && <p className={`text-[10px] ml-[3.4rem] -mt-1 mb-1 ${brevoLine.tone}`}>{brevoLine.text}</p>}
      <Row icon={<IconPhone className="w-3.5 h-3.5" />} label="Telephone" value={tp.telephone.value} onSave={save('telephone')} href={tp.telephone.value ? `tel:${tp.telephone.value}` : null} />
      <Row icon={<IconPhone className="w-3.5 h-3.5" />} label="Mobile" value={tp.mobile.value} onSave={save('mobile')} href={tp.mobile.value ? `tel:${tp.mobile.value}` : null} />
      <Row icon={<IconLink className="w-3.5 h-3.5" />} label="LinkedIn" value={tp.linkedin.value} onSave={save('linkedin')} href={tp.linkedin.value && /^https?:\/\//.test(tp.linkedin.value) ? tp.linkedin.value : tp.linkedin.value ? `https://${tp.linkedin.value}` : null} />
      <Row icon={<IconChatUi className="w-3.5 h-3.5" />} label="WhatsApp" value={tp.whatsapp.value} hint={tp.whatsapp.assumedFromPhone ? 'assumed from phone' : undefined} onSave={save('whatsapp')}
        href={tp.whatsapp.value ? `https://wa.me/${tp.whatsapp.value.replace(/[^\d]/g, '')}` : null} />
    </div>
  )
}
